import { useState, useEffect, useMemo } from 'react';
import { addDays, ptYesterday, ymdToLocalDate } from '@/shared/ptDates';
import { supabase, invokeFunction } from '@/integrations/supabase/client';

interface SyncCheckResult {
  latestSyncedDate: Date;
  allSynced: boolean;
  loading: boolean;
}

/**
 * Checks whether today's Fivetran sync has completed for ALL connected
 * Fivetran connectors. If not, the latest safe end-date is pushed back
 * by one day so graphs only show fully-synced data.
 *
 * Uses a server-side edge function to bypass RLS restrictions on the
 * fivetran_webhook_events table.
 */
export const useSyncCompletionCheck = (organisation: any): SyncCheckResult => {
  const [allSynced, setAllSynced] = useState(false);
  const [loading, setLoading] = useState(true);

  const organisationId = organisation?.id;

  useEffect(() => {
    if (!organisationId) {
      setAllSynced(true);
      setLoading(false);
      return;
    }

    let cancelled = false;

    const check = async () => {
      setLoading(true);
      try {
        const { data, error } = await invokeFunction(supabase, 
          'get-fivetran-sync-completion',
          { body: { organisationId } },
        );

        if (cancelled) return;

        if (error) {
          console.error('Error checking sync status:', error);
          setAllSynced(false);
        } else {
          console.log('Sync completion response:', data);
          setAllSynced(data?.allSynced ?? false);
        }
      } catch (err) {
        if (!cancelled) {
          console.error('Unexpected error checking sync status:', err);
          setAllSynced(false);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    check();
    return () => { cancelled = true; };
  }, [organisationId]);

  // Dates are PT calendar dates (FIG-429): the figures end at the end of yesterday in Pacific time.
  const latestSyncedDate = allSynced
    ? ymdToLocalDate(ptYesterday()) // yesterday PT — today's sync brought in yesterday's full data
    : ymdToLocalDate(addDays(ptYesterday(), -1)); // day before yesterday PT — safe last complete date

  return { latestSyncedDate, allSynced, loading };
};
